import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgendaMotionStats } from '../../types/types';
import { getAgendaMotionStats, getScope } from '../../data/cache';
import useUrlParams from '../../hooks/useUrlParams';
import { knessetFromParam, previousKnesset, swornInLabel, useScope } from '../../hooks/useScope';
import { formatDayMonth, useSyncPause } from '../../hooks/useLastSync';
import { useBreakpoint } from '../../useBreakpoint';
import { buttonReset, color, font, space, weight } from '../../theme';
import TwoPane from '../../layout/TwoPane';
import Grid from '../../layout/Grid';
import FilterSection from '../../layout/FilterSection';
import PageHeader from '../PageHeader';
import KnessetMenu from '../KnessetMenu';
import Notice from '../Notice';
import SearchField from '../SearchField';
import Chip from '../Chip';
import SelectMenu, { ResultBar, SelectOption } from '../SelectMenu';
import { Card } from '../Card';
import Button from '../Button';
import { EmptyState, ErrorState } from '../States';
import { SkeletonList } from '../Skeleton';
import AgendaMotionRow from './AgendaMotionRow';
import {
  AgendaMotionData, loadAgendaMotionData, MOTION_TYPE_SHORT, MOTION_TYPES, motionMatches, STATUS_GROUPS, StatusGroup, statusGroup,
} from './agendaMotions';

const PAGE_SIZE = 30;
// Rows without a type are counted under this label (as meta/agendaMotionStats does)
const OTHER = 'אחר';

type SortKey = 'new' | 'old';
const SORT_OPTIONS: SelectOption<SortKey>[] = [
  { key: 'new', label: 'מהחדשה לישנה' },
  { key: 'old', label: 'מהישנה לחדשה' },
];

/* The motions for the agenda list (5d-A): the הצעות לסדר היום tab of /queries. Search by
   subject or MK, filter by type and by status group (chips: the Knesset's statuses in four
   groups, agendaMotions.ts statusGroup) and committee (a menu), newest or oldest first, 30 at
   a time. Everything is in the URL (?tab=motions&q=&type=&sg=&cmte=&sort=&n=), as is the
   Knesset (?k=, archived Knessets through the KnessetMenu). Desktop: the search and a filter
   card in a column beside the list (as on Legislation, but not sticky: see below).
   `switcher`: the page's שאילתות / הצעות לסדר היום control, shown under the title. */
const AgendaMotionList = ({ switcher }: { switcher: React.ReactNode }) => {
  const [params, setParams] = useUrlParams();
  const { isDesktop } = useBreakpoint();
  const scope = useScope();
  const pausedSince = useSyncPause('queries');
  const [data, setData] = useState<AgendaMotionData | null>(null);
  const [stats, setStats] = useState<AgendaMotionStats | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // The Knesset shown: the current one, or an archived one (?k=). The data load resolves it
  // from the loaded scope, so a first render's default scope never loads the wrong list.
  const kParam = params.get('k');
  const viewKnesset = knessetFromParam(kParam, scope);
  const viewingCurrent = viewKnesset === scope.knesset;
  const previous = previousKnesset(scope);
  const search = params.get('q') || '';
  const type = params.get('type') || 'all';
  const sgParam = params.get('sg');
  const sg: StatusGroup | 'all' = STATUS_GROUPS.some(g => g.key === sgParam) ? (sgParam as StatusGroup) : 'all';
  const cmte = params.get('cmte') || 'all';
  const sort: SortKey = params.get('sort') === 'old' ? 'old' : 'new';
  const visibleCount = parseInt(params.get('n') || '', 10) || PAGE_SIZE;

  const update = useCallback((changes: Record<string, string | null>) => setParams(prev => {
    const next = new URLSearchParams(prev);
    const keys = Object.keys(changes);
    for (let i = 0; i < keys.length; i++) {
      const v = changes[keys[i]];
      if (v === null || v === '') next.delete(keys[i]); else next.set(keys[i], v);
    }
    return next;
  }), [setParams]);
  // Another Knesset is a fresh list: filters and paging reset
  const setKnesset = (k: number) => setParams(() => {
    const next = new URLSearchParams();
    next.set('tab', 'motions');
    if (k !== scope.knesset) next.set('k', String(k));
    return next;
  });
  const clearFilters = () => update({ q: null, type: null, sg: null, cmte: null, n: null });

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setData(null);
    getScope()
      .then(sc => {
        const k = knessetFromParam(kParam, sc);
        return Promise.all([loadAgendaMotionData(k), getAgendaMotionStats(k).catch(() => null)]);
      })
      .then(([d, st]) => { if (!cancelled) { setData(d); setStats(st); } })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [kParam, reloadKey]);

  // Scroll restoration: save on unmount, restore once the list has loaded
  const restored = useRef(false);
  useEffect(() => () => {
    try { sessionStorage.setItem('motions_scroll', String(window.scrollY)); } catch { /* ok */ }
  }, []);
  useEffect(() => {
    if (restored.current || !data) return;
    restored.current = true;
    try {
      const saved = sessionStorage.getItem('motions_scroll');
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem('motions_scroll'); }
    } catch { /* ok */ }
  }, [data]);

  // The live list describes the Knesset in its stats doc: right after a new Knesset starts it
  // may still be the previous one's, which isn't this Knesset's list
  const staleLive = viewingCurrent && !!stats && typeof stats.knessetNumber === 'number' && stats.knessetNumber !== scope.knesset;
  const rows = useMemo(() => (data && !staleLive ? data.motions : []), [data, staleLive]);
  const members = useMemo(() => (data ? data.members : {}), [data]);
  const committeeNames = data ? data.committees : {};

  const counts = useMemo(() => {
    const byType: Record<string, number> = {};
    const byGroup: Record<string, number> = {};
    const byCommittee: Record<string, number> = {};
    rows.forEach(m => {
      const t = m.type || OTHER;
      const g = statusGroup(m.status);
      const c = m.committeeId || 'none';
      byType[t] = (byType[t] || 0) + 1;
      byGroup[g] = (byGroup[g] || 0) + 1;
      byCommittee[c] = (byCommittee[c] || 0) + 1;
    });
    return { byType, byGroup, byCommittee };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim();
    const list = rows.filter(m =>
      (type === 'all' || (m.type || OTHER) === type) &&
      (sg === 'all' || statusGroup(m.status) === sg) &&
      (cmte === 'all' || (cmte === 'none' ? !m.committeeId : m.committeeId === cmte)) &&
      motionMatches(m, q, members));
    // The list is newest first; "oldest first" flips it
    return sort === 'old' ? list.slice().reverse() : list;
  }, [rows, type, sg, cmte, search, sort, members]);

  const total = rows.length;
  const visible = filtered.slice(0, visibleCount);
  const hasFilters = type !== 'all' || sg !== 'all' || cmte !== 'all' || search.trim() !== '';
  const earlyEmpty = !!data && total === 0 && viewingCurrent && previous !== null;
  const sortPhrase = sort === 'old' ? 'מהישנה לחדשה' : 'מהחדשה לישנה';

  /* ── Filters ── */
  // The four types in their order (and any other the data has), when the list has them
  const types = MOTION_TYPES.filter(t => counts.byType[t] || t === type)
    .concat(Object.keys(counts.byType).filter(t => MOTION_TYPES.indexOf(t) < 0));
  const pickType = (t: string) => update({ type: t === 'all' || t === type ? null : t, n: null });
  const pickGroup = (g: StatusGroup | 'all') => update({ sg: g === 'all' || g === sg ? null : g, n: null });
  const committeeOptions: SelectOption<string>[] = [{ key: 'all', label: 'כל הוועדות', count: total }].concat(
    Object.keys(counts.byCommittee).filter(c => c !== 'none')
      .sort((a, b) => counts.byCommittee[b] - counts.byCommittee[a])
      .map(c => ({ key: c, label: committeeNames[c] || 'ועדה', count: counts.byCommittee[c] })),
    counts.byCommittee.none ? [{ key: 'none', label: 'ללא ועדה', count: counts.byCommittee.none }] : []);
  // Phone: a chip ("ועדה ▾"); desktop: "ועדה: כל הוועדות ▾" in the filter card
  const committeeMenu = (
    <SelectMenu variant={isDesktop ? 'text' : 'chip'} label="ועדה" value={committeeOptions.some(o => o.key === cmte) ? cmte : 'all'} neutralValue="all" align="start"
      onChange={c => update({ cmte: c === 'all' ? null : c, n: null })} options={committeeOptions} />
  );
  const searchField = <SearchField value={search} onChange={v => update({ q: v, n: null })} placeholder="חיפוש לפי נושא או ח״כ" />;
  const clearButton = (
    <button type="button" onClick={clearFilters} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, fontSize: font.small, minHeight: 32 }}>ניקוי הסינון</button>
  );

  const filters = isDesktop ? (
    // Desktop: search, then one card with a labelled section per kind of filter
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
      {searchField}
      <Card>
        <FilterSection label="סוג הצעה">
          <Chip variant="flat" block selected={type === 'all'} onClick={() => pickType('all')} count={total}>כל הסוגים</Chip>
          <Grid cols={{ desktop: 2 }} gap={6}>
            {types.map(t => <Chip key={t} variant="flat" block selected={type === t} onClick={() => pickType(t)} count={counts.byType[t] || 0}>{t}</Chip>)}
          </Grid>
        </FilterSection>
        <FilterSection label="סטטוס">
          <Chip variant="flat" block selected={sg === 'all'} onClick={() => pickGroup('all')} count={total}>כל הסטטוסים</Chip>
          <Grid cols={{ desktop: 2 }} gap={6}>
            {STATUS_GROUPS.map(g => <Chip key={g.key} variant="flat" block selected={sg === g.key} onClick={() => pickGroup(g.key)} count={counts.byGroup[g.key] || 0}>{g.label}</Chip>)}
          </Grid>
        </FilterSection>
        <FilterSection label="ועדה"><div style={{ display: 'flex' }}>{committeeMenu}</div></FilterSection>
        <FilterSection label="מיון" last>
          <Grid cols={{ desktop: 2 }} gap={6}>
            <Chip variant="flat" block selected={sort === 'new'} onClick={() => update({ sort: null })}>מהחדשה</Chip>
            <Chip variant="flat" block selected={sort === 'old'} onClick={() => update({ sort: 'old' })}>מהישנה</Chip>
          </Grid>
        </FilterSection>
      </Card>
    </div>
  ) : (
    // Phone and tablet: search, the type chips, the status chips, the committee menu, the result line
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
      {searchField}
      <div role="group" aria-label="סוג הצעה" style={{ display: 'flex', flexWrap: 'wrap', columnGap: space.sm }}>
        <Chip selected={type === 'all'} onClick={() => pickType('all')} count={total}>הכל</Chip>
        {types.map(t => <Chip key={t} selected={type === t} onClick={() => pickType(t)} count={counts.byType[t] || 0}>{MOTION_TYPE_SHORT[t] || t}</Chip>)}
      </div>
      <div role="group" aria-label="סטטוס" style={{ display: 'flex', flexWrap: 'wrap', columnGap: space.sm }}>
        <Chip selected={sg === 'all'} onClick={() => pickGroup('all')} count={total}>כל הסטטוסים</Chip>
        {STATUS_GROUPS.map(g => <Chip key={g.key} selected={sg === g.key} onClick={() => pickGroup(g.key)} count={counts.byGroup[g.key] || 0}>{g.label}</Chip>)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: space.sm }}>
        {committeeMenu}
        {hasFilters && <span style={{ marginInlineStart: 'auto' }}>{clearButton}</span>}
      </div>
      <ResultBar action={<SelectMenu label="מיון" value={sort} onChange={s => update({ sort: s === 'old' ? 'old' : null })} options={SORT_OPTIONS} />}>
        {hasFilters ? `${filtered.length.toLocaleString('he-IL')} מתוך ${total.toLocaleString('he-IL')} הצעות` : `${total.toLocaleString('he-IL')} הצעות`}
      </ResultBar>
    </div>
  );

  return (
    <>
      <PageHeader
        title="הצעות לסדר היום"
        subtitle={data && !earlyEmpty
          ? `${total.toLocaleString('he-IL')} הצעות שהגישו חברי הכנסת ה-${viewKnesset}`
          : `הצעות לסדר היום שהגישו חברי הכנסת ה-${viewKnesset}`}
        action={<KnessetMenu value={viewKnesset} onChange={setKnesset} />}
      >
        {switcher}
      </PageHeader>

      {viewingCurrent && pausedSince && (
        <Notice tone="warning" icon="clock">
          <b>הנתונים נכונים ל-{formatDayMonth(pausedSince)}.</b> הכנסת ה-{scope.knesset + 1} הושבעה, והעדכון יחודש כשהאתר יעבור אליה.
        </Notice>
      )}

      {error && <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>}

      {!error && !data && <SkeletonList rows={6} />}

      {/* A new Knesset with no motions yet: say so and offer the previous Knesset's */}
      {earlyEmpty && previous !== null && (
        <Card padding={0}>
          <EmptyState
            icon="document"
            title="עוד לא הוגשו הצעות לסדר היום"
            body={`הכנסת ה-${scope.knesset} הושבעה${swornInLabel(scope) ? ` ב-${swornInLabel(scope)}` : ''}. הצעות לסדר היום יופיעו כאן כשחברי הכנסת יתחילו להגיש אותן.`}
            action={<Button variant="primary" onClick={() => setKnesset(previous)}>להצעות של הכנסת ה-{previous}</Button>}
          />
        </Card>
      )}

      {data && !earlyEmpty && total === 0 && (
        <Card padding={0}><EmptyState icon="document" title="אין הצעות לסדר היום להצגה" /></Card>
      )}

      {data && total > 0 && (
        // Not sticky on desktop: the sticky pane scrolls (overflow), which would clip the committee menu's popover
        <TwoPane sideWidth={300} gap={space.xxl} side={filters} stickySide={false}>
          {isDesktop && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: font.small, color: color.textSecondary, marginBottom: space.sm }}>
              <span role="status">{hasFilters
                ? `${filtered.length.toLocaleString('he-IL')} תוצאות מתוך ${total.toLocaleString('he-IL')}`
                : `${total.toLocaleString('he-IL')} הצעות, ${sortPhrase}`}</span>
              {hasFilters && clearButton}
            </div>
          )}
          {filtered.length === 0
            ? (
              <Card padding={0}>
                <EmptyState title="לא נמצאו הצעות" action={<Button variant="ghost" onClick={clearFilters}>הצגת כל ההצעות</Button>} />
              </Card>
            )
            : (
              <Card padding={0}>
                {visible.map((m, i) => (
                  <AgendaMotionRow key={m.id} motion={m} members={members} committees={committeeNames} divider={i > 0} from="הצעות לסדר היום" />
                ))}
              </Card>
            )}
          {visible.length < filtered.length && (
            <div style={isDesktop ? { marginTop: space.md } : undefined}>
              <Button fullWidth onClick={() => update({ n: String(visibleCount + PAGE_SIZE) })}>
                הצגת עוד ({(filtered.length - visible.length).toLocaleString('he-IL')} נותרו)
              </Button>
            </div>
          )}
        </TwoPane>
      )}
    </>
  );
};

export default AgendaMotionList;
